import React, { useState, useEffect } from "react";
import {
  formatSecondsToHumanTime,
  parseTempoRestanteToSeconds,
  isSLAPausado,
} from "../../utils/chamadoUtils";

interface CountdownTimerProps {
  tempoRestante: string;
  slaVencido?: boolean;
  statusSLA?: string;
  statusChamado?: string;
  className?: string;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  tempoRestante,
  slaVencido = false,
  statusSLA,
  statusChamado = "",
  className = "",
}) => {
  const [segundosRestantes, setSegundosRestantes] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Verificar se SLA está pausado
  const slaPausado =
    isSLAPausado(statusChamado) || statusSLA?.toLowerCase().includes("pausado");

  useEffect(() => {
    const segundosIniciais = parseTempoRestanteToSeconds(tempoRestante);
    setSegundosRestantes(segundosIniciais);
    setIsInitialized(true);
  }, [tempoRestante]);

  useEffect(() => {
    if (!isInitialized || slaPausado || slaVencido) {
      return;
    }

    // Só inicia o timer se houver tempo restante
    if (segundosRestantes > 0) {
      const interval = setInterval(() => {
        setSegundosRestantes((prev) => {
          const newValue = prev - 1;
          return newValue < 0 ? 0 : newValue;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isInitialized, slaPausado, slaVencido, segundosRestantes]);

  // Classes CSS baseadas no estado
  const getStatusClasses = () => {
    if (slaVencido) {
      return "text-red-600 bg-red-50 border-red-200";
    } else if (slaPausado) {
      return "text-orange-600 bg-orange-50 border-orange-200";
    } else if (segundosRestantes <= 3600) {
      // Menos de 1 hora
      return "text-red-500 bg-red-50 border-red-200";
    } else if (segundosRestantes <= 14400) {
      // Menos de 4 horas
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    } else {
      return "text-green-600 bg-green-50 border-green-200";
    }
  };

  const renderContent = () => {
    if (slaVencido) {
      return <span className="font-semibold text-xs">SLA Vencido</span>;
    }

    if (slaPausado) {
      return (
        <div className="flex flex-col">
          <span className="font-semibold text-xs">SLA Pausado</span>
          {segundosRestantes > 0 && (
            <span className="text-xs opacity-75">
              Tempo Restante: {formatSecondsToHumanTime(segundosRestantes)}
            </span>
          )}
        </div>
      );
    }

    if (segundosRestantes <= 0 && !slaVencido) {
      return <span className="text-xs">Vencido</span>;
    }

    return (
      <span className="text-xs">
        {formatSecondsToHumanTime(segundosRestantes)}
      </span>
    );
  };

  // Se está no modo transparente (integrado às tags), retorna apenas o conteúdo sem wrapper
  if (className.includes("bg-transparent")) {
    return <>{renderContent()}</>;
  }

  return (
    <div
      className={`inline-flex items-center px-2 py-1 rounded-md border text-xs ${getStatusClasses()} ${className}`}
    >
      {renderContent()}
    </div>
  );
};
